/**
 * @fileoverview Pipe-network theory drawer — the supportive information behind
 * the pipe editor: Darcy–Weisbach friction and why pressure drop scales with
 * the diameter to the fifth power (Δp ∝ L/D⁵). Mirrors the ScoresDrawer pattern.
 *
 * @module HydraulicBalancingTesting/components/PipeTheoryDrawer
 */

import React from 'react';
import { Drawer } from 'antd';

import { LatexRenderer } from '../../../Shared/LatexRenderer';

import styles from '../HydraulicBalancingTesting.module.css';

const BLOCKS = [
  { type: 'text', text: 'Every metre of pipe, every bend and every valve resists flow. For the turbulent flow in a heating circuit the pressure loss is quadratic in the flow rate — and extremely sensitive to the pipe bore.' },

  { type: 'subtitle', text: 'Darcy–Weisbach friction' },
  { type: 'text', text: 'The pressure drop along a pipe of length L and inner diameter D, carrying water (density ρ) at mean velocity v, is:' },
  { type: 'latex', latex: '$ \\Delta p = f\\,\\dfrac{L}{D}\\,\\dfrac{\\rho v^{2}}{2} $' },
  { type: 'text', text: 'f is the (here constant) friction factor. On its own this already says loss grows linearly with length and inversely with diameter — but the velocity term hides a much stronger diameter dependence.' },

  { type: 'subtitle', text: 'Why diameter enters to the 5th power' },
  { type: 'text', text: 'Velocity is fixed by the volume flow and the cross-section A = πD²/4, so for a given flow the water must move faster through a narrower pipe:' },
  { type: 'latex', latex: '$ v = \\dfrac{\\dot V}{A} = \\dfrac{4\\dot V}{\\pi D^{2}} \\;\\propto\\; \\dfrac{\\dot V}{D^{2}} $' },
  { type: 'text', text: 'Substituting v back into Darcy–Weisbach:' },
  { type: 'latex', latex: '$ \\Delta p = f\\,\\dfrac{L}{D}\\,\\dfrac{\\rho}{2}\\left(\\dfrac{4\\dot V}{\\pi D^{2}}\\right)^{2} = \\dfrac{8 f \\rho}{\\pi^{2}}\\,\\dfrac{L\\,\\dot V^{2}}{D^{5}} $' },
  { type: 'callout', text: '⇒ Δp ∝ L / D⁵.  Halving a pipe\'s bore multiplies its resistance by 2⁵ = 32×; going one size up can cut a branch\'s loss several-fold. Diameter is by far the strongest lever in the network — that is why the editor lets you tune it per segment.' },

  { type: 'subtitle', text: 'Valves (Kv)' },
  { type: 'text', text: 'A valve is rated by its flow coefficient Kv (the flow in m³/h it passes at 1 bar). It is just another quadratic resistance:' },
  { type: 'latex', latex: '$ \\dot V = K_v\\sqrt{\\Delta p}\\quad\\Rightarrow\\quad \\Delta p \\,[\\text{bar}] = \\left(\\dfrac{\\dot V}{K_v}\\right)^{2} $' },
  { type: 'text', text: 'The presettable thermostatic valve adds a second, adjustable Kv — that is the knob you use to balance each branch.' },

  { type: 'subtitle', text: 'Combining the network' },
  { type: 'text', text: 'Bends, tees and the lockshield add an equivalent length (here a ×1.3 fitting factor, applied to supply + return). Resistances add in series along a path and combine as conductances (c = 1/√r) in parallel across branches. Because all branches share one pump, changing any single pipe or valve redistributes the flow everywhere — balancing is therefore iterative.' },

  { type: 'subtitle', text: 'Velocity & noise' },
  { type: 'text', text: 'Keep branch velocity below ≈0.45 m/s; above it valves and fittings start to whistle. Since v ∝ 1/D², widening a pipe is the quietest way to carry more flow.' },
];

const Block = ({ block }) => {
  switch (block.type) {
    case 'text':
      return <p className={styles.theoryText}>{block.text}</p>;
    case 'subtitle':
      return <p className={styles.sectionLabel} style={{ marginTop: 14 }}>{block.text}</p>;
    case 'latex':
      return (
        <div className={styles.theoryFormula}>
          <LatexRenderer latex={block.latex} />
        </div>
      );
    case 'callout':
      return <p className={styles.theoryCallout}>{block.text}</p>;
    default:
      return null;
  }
};

export const PipeTheoryDrawer = ({ open, onClose }) => (
  <Drawer
    title="Pipe network — theory (friction, Kv and the D⁵ rule)"
    placement="right"
    width={672}
    open={open}
    onClose={onClose}
    zIndex={1100}
  >
    {BLOCKS.map((block, i) => (
      <Block key={i} block={block} />
    ))}
  </Drawer>
);
